import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { TableCell } from '@mui/material';
import {
  Typography,
  TableWrapperFooter,
  TableHeadCell,
  TableIconCell,
  TableDots,
  Pagination,
  Chip,
  Button,
  ContainerAvatar,
  enqueueSnackbar,
  containerEditModal,
  containerTransferModal,
  containerSellModal,
  uploadImagesModal,
  Checkbox,
  TableIconsCell,
  repairSetUpdateMultipleModal,
  TableCellWrapperCenter,
  shareImagesModal,
  SimpleTable,
  containerListModal,
  containerUpdateHoldModal,
  CheckboxLabel,
} from '../../design-system';
import {
  DashboardHeader,
  FilterIconButton,
  CardActions,
  Cards,
  CardWrapper,
  CardsWrapper,
  CardsActions,
} from '../dashboard';
import { Filters } from './Filters';
import { Card } from './Cards';
import { MobileFilters } from './MobileFilters';
import { useStore } from '../../context';
import {
  momentTextFormat,
  momentDiffDays,
  showToRoles,
  formatContainerType,
  filterWithUserId,
  getContainerOptions,
  showToRolesMobile,
} from '../../utils';
import { getNotFoundContainers } from './utils';
import {
  useStatesWithFilterCounter,
  useSort,
  usePaginationState,
  useContainersQuery,
  useIsSmallScreen,
  smallScreenSize,
  clearSavedFilters,
  useSaveFilters,
} from '../../hooks';
import { initialGateOutsAddProps } from '../gate-outs';
import {
  TABLE,
  MESSAGES,
  ContainerStatusToChipLabels,
  ContainerStatusToChipColors,
  ContainerConditionToChipLabels,
  ContainerConditionToChipColors,
} from '../../const';
import { BelongsTo, ContainerGetAllQuery } from '../../types';
import { containersService } from '../../services';
import { i18n } from '../../i18n';
import { handleExportExcelClick } from './handle-export-excel-click';

let openFilterPopup: any = () => null;
let openContainerListPopup: any = () => null;
let cancelContainerList: any = () => null;

interface ContainersPageProps {
  innerScroll?: boolean;
}

export const ContainersPage = styled.div<ContainersPageProps>`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: 3rem;

  height: 100%;

  ${(props) =>
    props.innerScroll &&
    css`
      overflow: hidden;

      @media (max-width: ${smallScreenSize}px) {
        overflow: initial;
      }
    `}

  @media (max-width: ${smallScreenSize}px) {
    grid-row-gap: var(--spacing-3);

    & > nav > * {
      justify-self: center;
    }
  }
`;

export const ContainersHeader = ({
  totalCount,
  containerList,
  setContainerList,
  handleExportExcelClick,
}: any) => {
  const {
    state: {
      user: { data: user },
      tutorial,
    },
  } = useStore();
  const isSmallScreen = useIsSmallScreen();

  return (
    <DashboardHeader id="containers-header" isSmallScreen={isSmallScreen}>
      <Typography variant={isSmallScreen ? 'textL' : 'textXL'} weight={'bold'}>
        {i18n.t('pages.containers.title')}: {totalCount}
      </Typography>
      {!isSmallScreen && handleExportExcelClick && (
        <Button size={'medium'} onClick={handleExportExcelClick} disabled={!totalCount}>
          {i18n.t('button.exportExcel')}
        </Button>
      )}
      {!isSmallScreen && containerList && (
        <Button
          size={'medium'}
          onClick={() => {
            cancelContainerList();
            setContainerList();
          }}
          data-color={'white'}
        >
          {i18n.t('button.cancel')}
        </Button>
      )}
      {(!isSmallScreen || tutorial.isActive) && !containerList && (
        <Button id="container-list-button" size={'medium'} onClick={openContainerListPopup}>
          {i18n.t('button.containerList')}
        </Button>
      )}
      {showToRoles('admin', user) && (
        <>
          <Button id={'gate-in-container-old'} size={'medium'} to={'/dashboard/containers/add'}>
            {i18n.t('button.gateInContainer')}
          </Button>
          <Button id={'gate-out-container-old'} size={'medium'} to={'/dashboard/containers/out'}>
            {i18n.t('button.gateOutContainer')}
          </Button>
        </>
      )}
      {showToRolesMobile('client', user, isSmallScreen) && (
        <FilterIconButton onClick={openFilterPopup} />
      )}
    </DashboardHeader>
  );
};

interface CheckboxActionsProps {
  data: ContainerGetAllQuery['data'];
  refetch: () => void;
  checkIndexs: number[];
  setCheckIndexs: (indexes: number[]) => void;
}

export const CheckboxActions = ({
  data,
  checkIndexs,
  setCheckIndexs,
  refetch,
}: CheckboxActionsProps) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  const filteredReleaseHoldData = data
    .filter((_: any, i: number) => checkIndexs.includes(i))
    .filter((item) => !!item.isHold && item.status === 'GATED_IN');

  const handleClickAction = (val: any) => {
    if (val === 'sell') {
      let message = i18n.t('label.some');
      let validatedData = data.filter((_: any, i: number) => checkIndexs.includes(i));

      if (validatedData.some((item) => !item.images.length)) {
        message = message + ' \n' + MESSAGES.CONTAINERS.WARNING.SALE_IMAGE;
      }
      if (validatedData.some((item) => item.status !== 'GATED_IN')) {
        message = message + ' \n' + MESSAGES.CONTAINERS.WARNING.SALE_STATUS;
      }
      if (validatedData.some((item) => !!item.sale)) {
        message = message + ' \n' + MESSAGES.CONTAINERS.WARNING.ALREADY_SALE;
      }
      if (validatedData.some((item) => !!item.isHold)) {
        message = message + ' \n' + MESSAGES.CONTAINERS.WARNING.SALE_HOLD_STATUS;
      }
      if (validatedData.some((item) => !item.canTrade)) {
        message = message + ' \n' + MESSAGES.CONTAINERS.WARNING.SALE_CAN_TRADE;
      }

      validatedData = validatedData.filter(
        (item) =>
          item.images.length &&
          item.status === 'GATED_IN' &&
          !item.sale &&
          !item.isHold &&
          item.canTrade,
      );

      if (message !== i18n.t('label.some') || !validatedData.length) {
        enqueueSnackbar(message, 'warning');
      }
      if (!validatedData.length) return;

      containerSellModal.data = validatedData.map((item) => ({
        id: item.id,
        number: item.number,
        yardId: item.clientYard.yard.id,
        type: item.type,
      }));

      containerSellModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.CONTAINERS.SUCCESS.SALE, 'success');
        refetch();
      };
      containerSellModal.open();
    }
    if (val === 'hold' || val === 'release-hold') {
      let message = i18n.t('label.some');
      let filteredData =
        val === 'hold'
          ? data.filter((_: any, i: number) => checkIndexs.includes(i))
          : filteredReleaseHoldData;

      if (val === 'hold') {
        if (filteredData.some((item) => !!item.isHold)) {
          message = message + ' \n' + MESSAGES.CONTAINERS.WARNING.HOLD_RELEASE_STATUS;
        }
        if (filteredData.some((item) => item.status !== 'GATED_IN')) {
          message = message + ' \n' + MESSAGES.CONTAINERS.WARNING.HOLD_STATUS_GATED_IN;
        }
        if (filteredData.some((item) => !!item.sale)) {
          message = message + ' \n' + MESSAGES.CONTAINERS.WARNING.HOLD_SALE_STATUS;
        }

        filteredData = data
          .filter((_: any, i: number) => checkIndexs.includes(i))
          .filter((item) => !item.isHold && item.status === 'GATED_IN' && !item.sale);

        if (message !== i18n.t('label.some') || !filteredData.length) {
          enqueueSnackbar(message, 'warning');
        }
        if (!filteredData.length) return;
      }

      containerUpdateHoldModal.data = filteredData.map((item) => ({
        id: item.id,
        number: item.number,
      }));
      containerUpdateHoldModal.isHold = val === 'hold';

      containerUpdateHoldModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(
          val === 'hold'
            ? MESSAGES.CONTAINERS.SUCCESS.HOLD
            : MESSAGES.CONTAINERS.SUCCESS.RELEASE_HOLD,
          'success',
        );
        refetch();
      };
      containerUpdateHoldModal.open();
    }
  };

  if (!checkIndexs.length) return <></>;

  return (
    <>
      {showToRoles(['SUPER_ADMIN', 'CLIENT_MANAGER', 'CLIENT_DISPATCHER'], user) && (
        <Button size={'medium'} data-color={'white'} onClick={() => handleClickAction('sell')}>
          {i18n.t('dropdown.markForSale')}
        </Button>
      )}
      <Button size={'medium'} data-color={'white'} onClick={() => handleClickAction('hold')}>
        {i18n.t('dropdown.holdContainer')}
      </Button>
      {!!filteredReleaseHoldData.length && (
        <Button
          size={'medium'}
          data-color={'white'}
          onClick={() => handleClickAction('release-hold')}
        >
          {i18n.t('dropdown.releaseHold')}
        </Button>
      )}
    </>
  );
};

export const ContainersBody = ({
  setData,
  setTotalCount,
  containerList,
  setContainerList,
  checkIndexs,
  setCheckIndexs,
  handleCheckboxChange,
  handleClickImage,
  setExportHandler,
  onPrintGateInReceipt,
  onPrintGateOutReceipt,
}: any) => {
  const {
    state: {
      user: { data: user },
      tutorial,
    },
  } = useStore();
  const isViewer = showToRoles(['YARD_COMPANY_VIEWER'], user);
  const isSmallScreen = useIsSmallScreen();
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const navigate = useNavigate();
  const { page, take, skip, handlePageChange } = usePaginationState();
  const [openDotsMenuIndex, setDotsMenuIndex] = useState(null);
  const [states, setStates, filteredStates] = useStatesWithFilterCounter(
    {
      country: '',
      city: '',
      yardId: '',
      clientId: '',
      number: '',
      type: 'All',
      condition: 'All',
      status: 'All',
      startDate: null,
      endDate: null,
    },
    {
      ...(user.belongsTo === BelongsTo.CLIENT ? { status: 'GATED_IN' } : {}),
    },
  );
  const { sortField, sortDirection, handleSort } = useSort({
    sortField: 'gateIn',
    sortDirection: 'desc',
  });
  const [filterKey, setFilterKey] = useSaveFilters('filters-key', true, Math.random());
  const {
    data = { data: [], totalCount: 0 },
    isFetching,
    refetch,
  } = useContainersQuery({
    ...filterWithUserId(filteredStates, user),
    take,
    skip,
    sortField,
    sortDirection,
  });

  useEffect(() => {
    if (!setExportHandler) return;
    setExportHandler(
      () => () =>
        handleExportExcelClick({
          states,
          user,
          filteredStates,
          totalCount: data.totalCount,
          sortField,
          sortDirection,
          containerList,
        }),
    );
    return () => setExportHandler(null);
  }, [setExportHandler, states, user, filteredStates, data.totalCount, sortField, sortDirection]);

  useEffect(() => {
    let oldContainerList = containerList;
    if (isFetching) return;

    if (!oldContainerList && typeof filteredStates.number !== 'string') {
      oldContainerList = filteredStates.number;
      setContainerList(oldContainerList);
    }
    setTotalCount(
      oldContainerList ? getNotFoundContainers(data, oldContainerList).totalCount : data.totalCount,
    );
    setData((oldContainerList ? getNotFoundContainers(data, oldContainerList) : data).data);
  }, [isFetching]);

  useEffect(() => {
    openFilterPopup = () => setIsOpenFilter(true);
    openContainerListPopup = () => {
      const fc = (fcProps = { containerNumber: [] }) => {
        const containerList = fcProps.containerNumber;
        if (!containerList.length) return;

        clearSavedFilters();
        setContainerList(containerList);
        setFilterKey(Math.random());
        setCheckIndexs([]);
        setStates('cancelall', {
          status: 'All',
          number: containerList,
        });
      };
      containerListModal.fc = fc;
      containerListModal.isFilterList = true;
      containerListModal.open();
    };
    cancelContainerList = () => {
      clearSavedFilters();
      setFilterKey(Math.random());
      setCheckIndexs([]);
      setStates('cancelall');
    };
  }, []);

  const handleDotsToggle = (i: any) => {
    if (openDotsMenuIndex !== i) return setDotsMenuIndex(i);

    setDotsMenuIndex(null);
  };

  const handleOnPageChange = (e: any, page: number) => {
    if (checkIndexs.length) setCheckIndexs([]);
    handlePageChange(e, page);
  };

  const handleFilterChange: typeof setStates = (...args) => {
    handleOnPageChange(null, 1);
    setStates(...args);
  };

  const handleDotsOptionSelect = (
    row: ContainerGetAllQuery['data'][number],
    number: any,
    val: any,
    id: any,
  ) => {
    if (val === 'edit') {
      Object.keys(containerEditModal).forEach((key) => {
        if (key !== 'open') delete containerEditModal[key];
      });
      containerEditModal.isSale = !!row.sale;
      containerEditModal.number = number;
      containerEditModal.type = row.type;
      containerEditModal.block = row.block || '';
      containerEditModal.row = row.row || '';
      containerEditModal.slot = row.slot || '';
      containerEditModal.tier = row.tier || '';
      containerEditModal.condition = row.condition || '';
      containerEditModal.licensePlateNumber = row.gateIn.driver?.licensePlateNumber;
      containerEditModal.val = val;
      containerEditModal.id = id;
      containerEditModal.clientYardId = row.clientYardId;
      containerEditModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.CONTAINERS.SUCCESS.EDITED, 'success');
        refetch();
      };
      containerEditModal.open();
    }
    if (val === 'addphotos' || val === 'adddocuments') {
      let oldFilesLength = 0;
      const key = val === 'addphotos' ? 'images' : val === 'adddocuments' ? 'documents' : '';
      if (key) oldFilesLength = row[key]?.length || 0;

      Object.keys(uploadImagesModal).forEach((key) => {
        if (key !== 'open') delete uploadImagesModal[key];
      });
      uploadImagesModal.number = number;
      uploadImagesModal.val = val;
      uploadImagesModal.id = id;
      uploadImagesModal.oldFilesLength = oldFilesLength;
      uploadImagesModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.CONTAINERS.SUCCESS.FILES_CHANGE, 'success');
        refetch();
      };
      uploadImagesModal.open();
    }
    if (val === 'requestgateout') {
      initialGateOutsAddProps.containerId = [id];
      initialGateOutsAddProps.containerNumber = [number];
      initialGateOutsAddProps.country = row.clientYard.yard.address.country;
      initialGateOutsAddProps.city = row.clientYard.yard.address.city;
      initialGateOutsAddProps.yardId = row.clientYard.yard.id;
      initialGateOutsAddProps.yardName = row.clientYard.yard.name;
      initialGateOutsAddProps.clientId = row.clientYard.client.id;
      initialGateOutsAddProps.clientName = row.clientYard.client.code;

      navigate('/dashboard/gateouts/add');
    }
    if (val === 'transfer') {
      containerTransferModal.containerId = id;
      containerTransferModal.number = number;
      containerTransferModal.yardId = row.clientYard.yard.id;
      containerTransferModal.clientId = row.clientYard.client.id;
      containerTransferModal.minDate = row.gateIn.completedAt;

      containerTransferModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.CONTAINERS.SUCCESS.TRANSFER, 'success');
        refetch();
      };
      containerTransferModal.open();
    }
    if (val === 'sell') {
      if (!row.images.length) {
        return enqueueSnackbar(MESSAGES.CONTAINERS.WARNING.SALE_IMAGE, 'warning');
      }

      containerSellModal.data = [
        {
          id: id,
          number: number,
          yardId: row.clientYard.yard.id,
          type: row.type,
        },
      ];

      containerSellModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.CONTAINERS.SUCCESS.SALE, 'success');
        refetch();
      };
      containerSellModal.open();
    }
    if (val === 'repair') {
      repairSetUpdateMultipleModal.containerId = id;
      repairSetUpdateMultipleModal.action = val;
      repairSetUpdateMultipleModal.number = number;

      repairSetUpdateMultipleModal.data = [
        {
          id,
          price: '0',
          expectedDurationDays: 0,
          description: '',
          beforeImages: [],
        },
      ];

      repairSetUpdateMultipleModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(MESSAGES.CONTAINERS.SUCCESS.REPAIR, 'success');
        refetch();
      };
      repairSetUpdateMultipleModal.open();
    }
    if (val === 'hold' || val === 'release-hold') {
      containerUpdateHoldModal.data = [{ id, number: row.number }];
      containerUpdateHoldModal.isHold = val === 'hold';

      containerUpdateHoldModal.submitClick = () => {
        setCheckIndexs([]);
        enqueueSnackbar(
          val === 'hold'
            ? MESSAGES.CONTAINERS.SUCCESS.HOLD
            : MESSAGES.CONTAINERS.SUCCESS.RELEASE_HOLD,
          'success',
        );
        refetch();
      };
      containerUpdateHoldModal.open();
    }
    if (val === 'download-images') {
      containersService.downloadImages(row.images, number);
    }
    if (val === 'share-images') {
      shareImagesModal.images = row.images;
      shareImagesModal.containerNumber = number;
      shareImagesModal.open();
    }
    if (val === 'print-gate-in-receipt') {
      setDotsMenuIndex(null);
      onPrintGateInReceipt(id);
    }
    if (val === 'print-gate-out-receipt') {
      setDotsMenuIndex(null);
      onPrintGateOutReceipt(id);
    }
  };

  const handleRowClick = (to: string) => {
    if (openDotsMenuIndex !== null) return;
    if (checkIndexs.length) return;
    navigate(to);
  };

  if (showToRolesMobile('client', user, isSmallScreen))
    return (
      <>
        <CardsWrapper hasActions={tutorial.isActive}>
          <CardsActions className="table-checkbox-actions">
            <CheckboxLabel
              indeterminate={
                !!checkIndexs.length &&
                checkIndexs.length !== getNotFoundContainers(data, containerList).data.length
              }
              checked={
                checkIndexs.length === getNotFoundContainers(data, containerList).data.length
              }
              onChange={() => handleCheckboxChange()}
              onClick={(e) => e.stopPropagation()}
              label={!checkIndexs.length ? i18n.t('label.all') : ''}
            />
            <CheckboxActions
              checkIndexs={checkIndexs}
              data={data.data}
              refetch={refetch}
              setCheckIndexs={setCheckIndexs}
            />
          </CardsActions>
          <Cards>
            {data.data.map((container, i) => (
              <CardWrapper key={i}>
                <CardActions isCenter>
                  {!!getContainerOptions(container, user).length && (
                    <TableDots
                      open={openDotsMenuIndex === i}
                      onToggle={() => handleDotsToggle(i)}
                      options={getContainerOptions(container, user)}
                      onSelect={(val) => {
                        handleDotsOptionSelect(container, container.number, val, container.id);
                      }}
                    />
                  )}
                </CardActions>
                <Card
                  handleRowClick={() => handleRowClick(`/dashboard/containers/${container.id}`)}
                  handleClickImage={handleClickImage}
                  handleDotsOptionSelect={handleDotsOptionSelect}
                  {...container}
                />
              </CardWrapper>
            ))}
          </Cards>
        </CardsWrapper>
        <Pagination
          count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
          siblingCount={0}
          page={page}
          onChange={handleOnPageChange}
        />
        <MobileFilters
          isOpen={isOpenFilter}
          setIsOpen={setIsOpenFilter}
          setStates={handleFilterChange}
          states={states}
          {...filteredStates}
        />
      </>
    );

  return (
    <SimpleTable
      data={containerList ? getNotFoundContainers(data, containerList) : data}
      filters={() => <Filters key={filterKey} setStates={handleFilterChange} {...states} />}
      checkboxActions={
        isViewer
          ? undefined
          : (data) => (
              <CheckboxActions
                data={data}
                checkIndexs={checkIndexs}
                setCheckIndexs={setCheckIndexs}
                refetch={refetch}
              />
            )
      }
      tableRowProps={(container, i) => ({
        key: container.number + i,
        onClick: container.id
          ? () => handleRowClick(`/dashboard/containers/${container.id}`)
          : () => {},
      })}
      columns={[
        {
          column: 1,
          head: () => (
            <TableHeadCell align={'left'} withGap>
              {!isViewer && (
                <Checkbox
                  indeterminate={
                    !!checkIndexs.length &&
                    checkIndexs.length !== getNotFoundContainers(data, containerList).data.length
                  }
                  checked={
                    checkIndexs.length === getNotFoundContainers(data, containerList).data.length
                  }
                  onChange={() => handleCheckboxChange()}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              {i18n.t('label.actions')}
            </TableHeadCell>
          ),
          body: (container, i) => (
            <TableIconsCell component={'th'} align={'left'}>
              {!isViewer && (
                <Checkbox
                  checked={checkIndexs.includes(i)}
                  onChange={() => handleCheckboxChange(i)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              {!!getContainerOptions(container, user).length && (
                <TableDots
                  open={openDotsMenuIndex === i}
                  onToggle={() => handleDotsToggle(i)}
                  options={getContainerOptions(container, user)}
                  onSelect={(val) => {
                    handleDotsOptionSelect(container, container.number, val, container.id);
                  }}
                />
              )}
            </TableIconsCell>
          ),
        },
        {
          head: () => <TableHeadCell align={'left'}>{i18n.t('label.photo')}</TableHeadCell>,
          body: (container) => (
            <TableIconCell component={'th'} align={'left'}>
              <ContainerAvatar images={container.images} />
            </TableIconCell>
          ),
        },
        {
          headText: () => i18n.t('label.client'),
          bodyText: (container) => container.clientYard?.client?.code,
          isHidden: () => !showToRoles('adminRead', user),
        },
        {
          headText: () => i18n.t('label.containerNumber'),
          bodyText: (container) => container.number,
        },
        {
          headText: () => i18n.t('label.type'),
          bodyText: (container) => formatContainerType(container.type),
        },
        {
          headText: () => i18n.t('label.condition'),
          body: (container) => (
            <TableCell align={'left'}>
              {container.condition && (
                <Chip
                  size={'small'}
                  label={ContainerConditionToChipLabels[container.condition]}
                  color={ContainerConditionToChipColors[container.condition]}
                />
              )}
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.marketStatus'),
          body: (container) => (
            <TableCell align={'left'}>
              {!!container.isHold && (
                <Chip
                  size={'small'}
                  label={ContainerStatusToChipLabels.HOLD}
                  color={ContainerStatusToChipColors.HOLD}
                />
              )}
              {!container.isHold && !!container.sale && (
                <Chip
                  size={'small'}
                  label={ContainerStatusToChipLabels[container.sale.status]}
                  color={ContainerStatusToChipColors[container.sale.status]}
                />
              )}
              {!container.isHold &&
                !container.sale &&
                container.canTrade &&
                container.status === 'GATED_IN' &&
                showToRoles(['SUPER_ADMIN', 'CLIENT_MANAGER', 'CLIENT_DISPATCHER'], user) && (
                  <Button
                    size={'small'}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDotsOptionSelect(container, container.number, 'sell', container.id);
                    }}
                  >
                    {i18n.t('button.sell')}
                  </Button>
                )}
            </TableCell>
          ),
          isHidden: (container) => !container.sale && !container.canTrade && !container.isHold,
        },
        {
          headText: () => i18n.t('label.status'),
          body: (container) => (
            <TableCellWrapperCenter align={'left'}>
              <Chip
                size={'small'}
                label={
                  ContainerStatusToChipLabels[
                    container.status === 'GATE_OUT_REQUESTED' &&
                    container.gateOut?.status === 'EXPIRED'
                      ? 'GATE_OUT_EXPIRED'
                      : container.status
                  ]
                }
                color={
                  ContainerStatusToChipColors[
                    container.status === 'GATE_OUT_REQUESTED' &&
                    container.gateOut?.status === 'EXPIRED'
                      ? 'GATE_OUT_EXPIRED'
                      : container.status
                  ]
                }
              />
              {!!container._count.repair && (
                <Chip size={'small'} label={i18n.t('label.chip.repairing')} color={'yellow'} />
              )}
            </TableCellWrapperCenter>
          ),
        },
        {
          head: () => (
            <TableHeadCell
              align={'left'}
              columnIdx={'gateIn'}
              sortField={sortField}
              sortDirection={sortDirection}
              handleSort={handleSort}
            >
              {i18n.t('label.gateInDate')}
            </TableHeadCell>
          ),
          bodyText: (container) =>
            container.gateIn.completedAt
              ? momentTextFormat(container.gateIn.completedAt, user)
              : '',
        },
        {
          head: () => (
            <TableHeadCell
              align={'left'}
              columnIdx={'gateOut'}
              sortField={sortField}
              sortDirection={sortDirection}
              handleSort={handleSort}
            >
              {i18n.t('label.gateOutDate')}
            </TableHeadCell>
          ),
          bodyText: (container) =>
            container.gateOut?.completedAt
              ? momentTextFormat(container.gateOut?.completedAt, user)
              : '',
        },
        {
          headText: () => i18n.t('label.daysInTerminal'),
          bodyText: (container) =>
            momentDiffDays(
              container.gateIn.completedAt,
              container.gateOut?.completedAt || new Date(),
            ) +
            ' ' +
            i18n.t('pages.containers.table.days'),
        },
      ]}
      footer={
        containerList
          ? undefined
          : () => (
              <TableWrapperFooter>
                <Pagination
                  count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
                  page={page}
                  onChange={handleOnPageChange}
                />
              </TableWrapperFooter>
            )
      }
    />
  );
};
